import { Page, PageStatus, Planogram } from "src/components";
import { MerchflowHeader } from "../../components/merchflows/MerchflowHeader/MerchflowHeader";
import { useParams } from "react-router-dom";
import { MerchflowStepsNavigator } from "src/components/merchflows/MerchflowStepsNavigator/MerchflowStepsNavigator";
import { Button, Color, Flex } from "src/elements";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAtom } from "jotai";
import { useApi } from "src/api";
import { useAsyncState } from "src/hooks/useAsyncState";
import { pagesS0PogAtom } from "./store/atoms";
import { useNavigation } from "src/utils/navigation";
import { pages } from "src/utils";
import { useLoaders } from "src/components/InlineLoader";
import { RoutePostMerchflows_merchflowId_S1 } from "@CommonApi/merchflows/_merchflowId_/s1";
import {
  ResponseGetMerchflows_merchflowId_S0_s0UserTriggerId_,
  RouteGetMerchflows_merchflowId_S0_s0UserTriggerId_,
} from "@CommonApi/merchflows/_merchflowId_/s0/_s0UserTriggerId_";
import { useMerchflowHeader } from "src/components/merchflows/MerchflowHeader/store/hooks";
import { calculateContainerAlgoPoints } from "@CommonUtils/pog/container";
import { S0LogsButton } from "./components/S0LogsButton";
import { Pog } from "@CommonTypes/merchflow/pog/pog";

export const PageS0 = () => {
  const { merchflowId: _merchflowId, s0UserTriggerId: _s0UserTriggerId } = useParams();
  const merchflowId = Number(_merchflowId);
  const s0UserTriggerId = Number(_s0UserTriggerId);

  const { getMerchflowS0Api, postStartMerchflowS1Api } = useApi();
  const { navigate } = useNavigation();
  const { hasLoader } = useLoaders();
  const { froggerVersion } = useMerchflowHeader();

  const refDataLoader = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [data, setData] = useAsyncState<ResponseGetMerchflows_merchflowId_S0_s0UserTriggerId_>();
  const [smallestSolution, setSmallestSolution] = useState<
    ResponseGetMerchflows_merchflowId_S0_s0UserTriggerId_[0] | null
  >(null);

  const [pog, setPog] = useAtom(pagesS0PogAtom);

  const isLoading = hasLoader(RouteGetMerchflows_merchflowId_S0_s0UserTriggerId_);
  const isGenerating = data?.find(
    (s0Solution) => s0Solution.status === "NOT_STARTED" || s0Solution.status === "IN_PROGRESS",
  );
  const isDone = smallestSolution?.status === "DONE";
  const isError = smallestSolution?.status === "ERROR";
  const isGeneratingS1 = hasLoader(RoutePostMerchflows_merchflowId_S1);

  // Effect to load s0 solutions.
  useEffect(() => {
    loadS0();

    return () => {
      if (refDataLoader.current) clearTimeout(refDataLoader.current);
      setPog(null);
    };
  }, []);

  // Effect to find smallest solution whenever it changes.
  useEffect(() => {
    const smallestS0 = data?.find(
      (s0) => s0.status === "DONE" && s0.solution.containers.length !== 0,
    );
    if (smallestS0) {
      setSmallestSolution(smallestS0);
    }
  }, [data]);

  // Effect to set pog and containers from smallest solution.
  useEffect(() => {
    if (smallestSolution?.status === "DONE") {
      setPog(new Pog(smallestSolution.solution));
    }
  }, [smallestSolution]);

  // Effect to check if min product heights are met
  const isMinProductsHeightMet = useMemo(() => {
    return (
      pog === null ||
      pog.containers.every(
        (container) => calculateContainerAlgoPoints(container, pog).minProductHeightMet,
      )
    );
  }, [pog]);

  const loadS0 = async () => {
    const response = await getMerchflowS0Api(merchflowId, s0UserTriggerId);
    setData(
      response?.sort((s0Solution1, s0Solution2) =>
        s0Solution1.template_code < s0Solution2.template_code ? -1 : 1,
      ),
    );

    // Keep checking for s0 after 5s cycles.
    if (
      response?.find(
        (s0Solution) => s0Solution.status === "NOT_STARTED" || s0Solution.status === "IN_PROGRESS",
      )
    ) {
      refDataLoader.current = setTimeout(() => {
        loadS0();
      }, 5000);
    }
  };

  const startGeneratingS1 = async () => {
    if (!pog || smallestSolution?.status !== "DONE") {
      return;
    }

    const response = await postStartMerchflowS1Api(merchflowId, {
      s0_user_trigger_id: s0UserTriggerId,
      s0_solution_id: smallestSolution.s0_solution_id,
      s0_solution: pog.toNonCircular(),
      frogger_version: froggerVersion,
    });

    if (response) {
      navigate(`${pages.merchflows_merchflowId_S1_s1UserTriggerId_Templates}`, {
        merchflowId,
        s1UserTriggerId: response.s1_user_trigger_id,
      });
    }
  };

  return (
    <Page>
      <MerchflowHeader merchflowId={merchflowId} s0UserTriggerId={s0UserTriggerId} />

      <MerchflowStepsNavigator
        merchflowId={merchflowId}
        s0UserTriggerId={s0UserTriggerId}
        activeStep="S0"
      />

      <Flex flexGrow>
        {(isGenerating && <PageStatus label="Generating CDT0 mudmap..." icon="spinner" />) ||
          (isLoading && <PageStatus label="Loading CDT0 mudmap..." icon="spinner" />) ||
          (data === null && (
            <PageStatus label="CDT0 mudmap not found" icon="alert" color={Color.red} />
          )) ||
          (isError && (
            <PageStatus label="CDT0 mudmap failed to generate" icon="alert" color={Color.red} />
          )) ||
          (isDone && <Planogram pogAtom={pagesS0PogAtom} isEditable isPanelVisible isWithBorder />)}
      </Flex>

      <Flex justify="right" gap="10px">
        {smallestSolution && (
          <S0LogsButton merchflowId={merchflowId} s0SolutionId={smallestSolution.s0_solution_id} />
        )}
        <Button
          isEnabled={!isGenerating && isDone && isMinProductsHeightMet}
          color={!isMinProductsHeightMet ? "red" : "primary"}
          isLoading={isGeneratingS1}
          onClick={startGeneratingS1}
        >
          Generate Base
        </Button>
      </Flex>
    </Page>
  );
};
